import React from "react";
import AppModal from "../AppModal/AppModal";
import { PostApiMethod, GetApiMethod } from "../../core/services/apiService";
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { setFetchedCustomerData, clearFetchedCustomerData } from '../../core/redux/settingsSlice';
import { useAppSelector, useAppDispatch } from "../../core/redux/hooks";
import CountryCodeDropdown from "./CountryCodeDropdown";
import MonumentTenantPicker from "./MonumentTenantPicker";
import { supportsPmsWriteBack } from "./pmsCapabilities";
import {
    MonumentUnitGroup,
    MonumentLeadSource,
    MonumentTenant,
    removeCountryCode,
    validatePhoneNumber,
    validateMonumentNewLead,
    monumentNewLeadCanAutoSubmit,
    getMonumentTargets,
    getMonumentTarget,
    buildMonumentUnitLabel,
    buildMonumentNewLeadPayload,
    buildMonumentExistingPayload,
} from "./monumentPayloads";
interface VideoCallCustomerDetailsProps {
    isOpen: boolean;
    onClose: (cancelledDuringCall?: boolean) => void;
    callSid?: string;
    locationId?: string;
    countdown?: number;
}

export interface VideoCallCustomerDetailsRef {
    autoSubmit: () => Promise<void>;
    getIsSubmitted: () => boolean;
    getIsCancelledDuringCall: () => boolean;
    resetCancelledDuringCall: () => void;
    getIsSuppressed: () => boolean;
}

// Monument payload builders, phone helpers, and validation now live in
// ./monumentPayloads so the voice and video modals cannot drift.

const VideoCallCustomerDetails = React.forwardRef<VideoCallCustomerDetailsRef, VideoCallCustomerDetailsProps>((
    { isOpen, onClose, callSid, locationId, countdown = 0 },
    ref
) => {
    const dispatch = useAppDispatch();
    const fetchedCustomerData = useAppSelector((state: any) => state.settings.fetchedCustomerData);
    const LoginUserDetails = useAppSelector((state: any) => state.auth.users);

    const [customerType, setCustomerType] = React.useState<"new" | "existing">("existing");
    // Monument support: the location's PMS provider, fetched on open (a video call has no
    // notification channel carrying it, unlike voice). Drives the Monument branch below.
    const [pmsProvider, setPmsProvider] = React.useState<string>("");
    const isMonument = pmsProvider === "monument";
    // The backend's write-back capability for this location (PMS_CAPABILITIES), delivered
    // on the same fetch as the provider. null until it resolves, or when talking to a
    // backend that predates the flag — supportsPmsWriteBack falls back to the provider name.
    const [writeBackFlag, setWriteBackFlag] = React.useState<boolean | null>(null);
    // A PMS with no write-back (today: Hummingbird) has nothing for this popup to write —
    // and Hummingbird also has no name search, while a video call carries no phone, so its
    // manual lookup could never resolve a customer either. Nothing to read, nothing to
    // write: suppress the popup entirely (render + submit guards below). Stays false until
    // the fetch resolves, so this only triggers once we positively know the capability.
    const isWriteBackSuppressed = !supportsPmsWriteBack({
        write_back_supported: writeBackFlag,
        pms_provider: pmsProvider,
    });
    // Whether the provider/unit-group fetch has resolved. Until it has, we do NOT know the
    // location's PMS, so an auto-submit must not guess (R4): guessing SSM for a Monument
    // location would send an SSM-shaped payload the write-back router silently drops.
    const [providerLoaded, setProviderLoaded] = React.useState(false);
    // Set when every retry of the provider fetch failed, so a manual submit can surface it
    // instead of silently mis-branching to the SSM UI.
    const [providerLoadError, setProviderLoadError] = React.useState(false);
    // Bumped to re-run the provider fetch on demand (the effect is keyed on locationId,
    // which does not change within a call — so a manual retry needs its own trigger).
    const [providerRetryNonce, setProviderRetryNonce] = React.useState(0);
    // Monument new-lead: the required unit-group dropdown (options fetched server-side —
    // the frontend has no facilityUuid) and the agent-entered phone (a video call carries
    // no caller number, and Monument rejects a lead with neither email nor phone).
    const [unitGroupUuid, setUnitGroupUuid] = React.useState("");
    const [unitGroups, setUnitGroups] = React.useState<MonumentUnitGroup[]>([]);
    // Optional lead-source attribution the agent picks (Phone / Website / Walk-In / …),
    // fetched alongside the provider + unit groups.
    const [leadSourceUuid, setLeadSourceUuid] = React.useState("");
    const [leadSources, setLeadSources] = React.useState<MonumentLeadSource[]>([]);
    // Monument new-lead address (docs mark these required; the live runtime does not).
    const [addressLine1, setAddressLine1] = React.useState("");
    const [addressLine2, setAddressLine2] = React.useState("");
    // Monument contact: a phone OR an email (Monument's create-lead needs one of them). A
    // video call carries no caller number, so both are agent-entered.
    const [phoneNumber, setPhoneNumber] = React.useState("");
    const [email, setEmail] = React.useState("");
    // Monument existing-tenant selection. A name search can match more than one distinct
    // Monument account (each with its own AccountUuid); the write-back note is account-level,
    // so the agent can pick which account it lands on (defaulting to the first). These hold the
    // distinct tenants and the chosen account id — kept separate from the SSM selectedCustomer*
    // state so neither path disturbs the other. See getMonumentTenants / MonumentTenantPicker.
    const [monumentTenants, setMonumentTenants] = React.useState<MonumentTenant[]>([]);
    // Prospects that matched, shown as their own picker section. A note picked here goes
    // to the LEAD, which is what stops the summary creating a second lead for someone
    // Monument already has.
    const [monumentLeads, setMonumentLeads] = React.useState<MonumentTenant[]>([]);
    const [selectedMonumentAccountUuid, setSelectedMonumentAccountUuid] = React.useState("");
    // Which unit the call was about, for a tenancy holding several. "" means the agent
    // did not narrow it and the note names every unit instead of guessing one.
    const [selectedMonumentUnitLabel, setSelectedMonumentUnitLabel] = React.useState("");
    // The tenant the write-back note attaches to. Defaults to the FIRST matched account when
    // none is explicitly picked (any count), so the flow never blocks and the note always has
    // a target; the picker (shown for >1 account, with the first pre-selected) lets the agent
    // override this default to the correct account.
    // Tenancies before prospects: the fallback below is what the call-end auto-submit uses
    // when the agent picked nothing, and an existing tenancy should outrank an enquiry.
    const monumentMatches: MonumentTenant[] = [...monumentTenants, ...monumentLeads];
    const selectedMonumentTenant: MonumentTenant | null =
        monumentMatches.find((t) => t.accountUuid === selectedMonumentAccountUuid) ||
        monumentMatches[0] || null;
    const [tenantDetails, setTenantDetails] = React.useState({
        tenantId: "",
        firstName: "",
        lastName: "",
        countryId: "1",
        facilityId: "",
        dateNeeded: "",
        advSource: "",
        classification: "",
        inquiryDetails: "",
        reservationDetails: "",
        rentalDetails: "",
    });
    const [dateNeeded, setDateNeeded] = React.useState<Date | null>(null);
    const [errors, setErrors] = React.useState<{ [key: string]: string }>({});
    const [isFetching, setIsFetching] = React.useState(false);
    const [customerOptions, setCustomerOptions] = React.useState<Array<{ value: string, label: string }>>([]);
    const [selectedCustomer, setSelectedCustomer] = React.useState("");
    const [selectedCustomerRecord, setSelectedCustomerRecord] = React.useState<any>(null);
    const [originalCustomerRecord, setOriginalCustomerRecord] = React.useState<any>(null);
    const [showVideoNoDataMessage, setShowVideoNoDataMessage] = React.useState(false);
    const [showNoCustomerFoundMessage, setShowNoCustomerFoundMessage] = React.useState(false);
    const [isSubmitted, setIsSubmitted] = React.useState(false);
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [apiError, setApiError] = React.useState<string>("");
    const [previousCallSid, setPreviousCallSid] = React.useState<string | undefined>(undefined);
    const [searchParams, setSearchParams] = React.useState<{ firstName: string; lastName: string } | null>(null);
    const [isCancelledDuringCall, setIsCancelledDuringCall] = React.useState(false);

    // FIX Issue #6: Reset isSubmitted when callSid changes (new call)
    React.useEffect(() => {
        setIsSubmitted(false);
        setApiError("");
        setIsCancelledDuringCall(false); // Reset cancelled flag for new call
    }, [callSid]);

    // Monument support: a video call has no notification channel carrying the provider,
    // so fetch it (and, for Monument, the new-lead unit groups) from the backend as soon
    // as we have a locationId. The endpoint resolves the facility server-side and returns
    // pms_provider plus an empty unit-group list for non-Monument locations.
    React.useEffect(() => {
        if (!locationId) { setPmsProvider(""); setWriteBackFlag(null); setUnitGroups([]); setLeadSources([]); setProviderLoaded(false); setProviderLoadError(false); return; }
        let cancelled = false;
        setProviderLoaded(false);
        setProviderLoadError(false);
        (async () => {
            // Retry a few times: a failed fetch would leave the provider unknown, and for a
            // Monument location that silently mis-branches the modal to the SSM UI (whose
            // payload the write-back router drops). R4: don't let one transient failure
            // strand the modal on the wrong provider.
            const MAX_ATTEMPTS = 3;
            for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
                try {
                    const res: any = await GetApiMethod(`tenant/sendCallSummary/monumentUnitGroups?locationId=${locationId}`);
                    if (cancelled) return;
                    setPmsProvider(res?.data?.pms_provider || "");
                    setWriteBackFlag(typeof res?.data?.write_back_supported === "boolean" ? res.data.write_back_supported : null);
                    setUnitGroups(Array.isArray(res?.data?.data) ? res.data.data : []);
                    setLeadSources(Array.isArray(res?.data?.leadSources) ? res.data.leadSources : []);
                    setProviderLoaded(true);
                    return;
                } catch (err) {
                    if (cancelled) return;
                    if (attempt === MAX_ATTEMPTS) {
                        console.error("[Monument] provider fetch failed after retries; the modal cannot confirm the location's PMS.", err);
                        setPmsProvider(""); setWriteBackFlag(null); setUnitGroups([]); setLeadSources([]);
                        setProviderLoadError(true);
                        setProviderLoaded(true);
                        return;
                    }
                    // brief backoff before the next attempt
                    await new Promise((r) => setTimeout(r, 800));
                    if (cancelled) return; // location changed / unmounted during backoff — don't fire another request
                }
            }
        })();
        return () => { cancelled = true; };
    }, [locationId, providerRetryNonce]);

    // Clear all state when call ends (callSid changes or becomes undefined)
    React.useEffect(() => {
        // Check if call has ended (callSid changed from a value to undefined or to a different value)
        if (previousCallSid && previousCallSid !== callSid) {
            console.log('Call ended, clearing all customer data');

            // Clear Redux store
            dispatch(clearFetchedCustomerData());

            // Reset all local state
            const defaultDate = new Date();
            defaultDate.setDate(defaultDate.getDate() + 10);
            setDateNeeded(defaultDate);
            setCustomerType("existing");
            setTenantDetails({
                tenantId: "",
                firstName: "",
                lastName: "",
                countryId: "1",
                facilityId: "",
                dateNeeded: defaultDate.toISOString().split("T")[0],
                advSource: "",
                classification: "",
                inquiryDetails: "",
                reservationDetails: "",
                rentalDetails: "",
            });
            setErrors({});
            setIsFetching(false);
            setCustomerOptions([]);
            setSelectedCustomer("");
            setSelectedCustomerRecord(null);
            setOriginalCustomerRecord(null);
            setMonumentTenants([]);
            setMonumentLeads([]);
            setSelectedMonumentAccountUuid("");
            setSelectedMonumentUnitLabel("");
            setShowVideoNoDataMessage(false);
            setShowNoCustomerFoundMessage(false);
            setIsSubmitted(false);
            setIsSubmitting(false);
            setApiError("");
            setSearchParams(null);
            // Monument new-lead fields (provider/unitGroups/leadSources are keyed on locationId).
            setUnitGroupUuid("");
            setLeadSourceUuid("");
            setPhoneNumber("");
            setEmail("");
            setAddressLine1("");
            setAddressLine2("");
        }

        // Update previous callSid for next comparison
        setPreviousCallSid(callSid);
    }, [callSid, previousCallSid, dispatch]);

    // Initialize when modal opens (but don't clear if during same call)
    React.useEffect(() => {
        if (isOpen) {
            // Check if we have fetched data from the same call
            const hasFetchedData = fetchedCustomerData && fetchedCustomerData.length > 0;

            if (hasFetchedData && searchParams) {
                // Restore the search form with previous firstName and lastName
                setTenantDetails(prev => ({
                    ...prev,
                    firstName: searchParams.firstName,
                    lastName: searchParams.lastName,
                }));

                // Rebuild customer options from combined data
                // Note: fetchedCustomerData[0] now contains all combined data from all records
                const options: Array<{ value: string, label: string }> = [];
                const uniqueCombinations = new Set<string>();

                const combinedRecord = fetchedCustomerData[0];
                const firstName = combinedRecord.FirstName || "";
                const lastName = combinedRecord.LastName || "";

                // Process all combined inquiries
                if (combinedRecord?.Inquiries && combinedRecord?.Inquiries?.length > 0) {
                    combinedRecord.Inquiries.forEach((inquiry: any) => {
                        const unitNumber = inquiry.UnitNumber || "N/A";
                        const combination = `${firstName}-${lastName}-${unitNumber}-Inquiry`;
                        if (!uniqueCombinations.has(combination)) {
                            uniqueCombinations.add(combination);
                            options.push({
                                value: combination,
                                label: `${firstName} ${lastName} - Unit ${unitNumber} - Inquiry`
                            });
                        }
                    });
                }

                // Process all combined reservations
                if (combinedRecord?.Reservations && combinedRecord.Reservations?.length > 0) {
                    combinedRecord.Reservations.forEach((reservation: any) => {
                        const unitNumber = reservation.UnitNumber || "N/A";
                        const combination = `${firstName}-${lastName}-${unitNumber}-Reservation`;
                        if (!uniqueCombinations.has(combination)) {
                            uniqueCombinations.add(combination);
                            options.push({
                                value: combination,
                                label: `${firstName} ${lastName} - Unit ${unitNumber} - Reservation`
                            });
                        }
                    });
                }

                // Process all combined rentals
                if (combinedRecord?.Rentals && combinedRecord?.Rentals?.length > 0) {
                    combinedRecord.Rentals.forEach((rental: any) => {
                        const unitNumber = rental.UnitNumber || "N/A";
                        const combination = `${firstName}-${lastName}-${unitNumber}-Rental`;
                        if (!uniqueCombinations.has(combination)) {
                            uniqueCombinations.add(combination);
                            options.push({
                                value: combination,
                                label: `${firstName} ${lastName} - Unit ${unitNumber} - Rental`
                            });
                        }
                    });
                }

                setCustomerOptions(options);
            } else if (!hasFetchedData) {
                // First open or new call - reset everything
                const defaultDate = new Date();
                defaultDate.setDate(defaultDate.getDate() + 10);
                setDateNeeded(defaultDate);

                setCustomerType("existing");
                setTenantDetails({
                    tenantId: "",
                    firstName: "",
                    lastName: "",
                    countryId: "1",
                    facilityId: "",
                    dateNeeded: defaultDate.toISOString().split("T")[0],
                    advSource: "",
                    classification: "",
                    inquiryDetails: "",
                    reservationDetails: "",
                    rentalDetails: "",
                });
                setIsSubmitted(false);
                setIsSubmitting(false);
                setApiError("");
                setErrors({});
                setCustomerOptions([]);
            }
        }
    }, [isOpen, fetchedCustomerData, searchParams]);

    const handleDateChange = (date: Date | null) => {
        setDateNeeded(date);
        setTenantDetails({
            ...tenantDetails,
            dateNeeded: date ? date.toISOString().split("T")[0] : ""
        });
    };

    // A search that resolves to no Monument match must drop the previous one: the picker
    // falls back to the first entry, so an unrelated card stays rendered AND selected and
    // becomes the write-back target for a customer the agent never searched for.
    const clearMonumentSelection = () => {
        setMonumentTenants([]);
        setMonumentLeads([]);
        setSelectedMonumentAccountUuid("");
        setSelectedMonumentUnitLabel("");
    };

    const handleFetchCustomer = async () => {
        if (!tenantDetails.firstName.trim() || !tenantDetails.lastName.trim()) {
            setErrors({
                firstName: !tenantDetails.firstName.trim() ? "First Name is required" : "",
                lastName: !tenantDetails.lastName.trim() ? "Last Name is required" : ""
            });
            return;
        }
        setErrors({});
        setIsFetching(true);
        try {
            const fetchPayload = {
                firstName: tenantDetails.firstName.trim(),
                lastName: tenantDetails.lastName.trim(),
                locationId: locationId,
                action: "fetch"
            };
            const res: any = await PostApiMethod("tenant/sendCallSummary/fetchCustomerDetails", fetchPayload);
            console.log("Fetch API Response:", res);

            // Store the API result
            const apiData = res?.data?.data || [];
            dispatch(setFetchedCustomerData(apiData));

            // Save search parameters for restoring later
            setSearchParams({
                firstName: tenantDetails.firstName.trim(),
                lastName: tenantDetails.lastName.trim()
            });

            // Check if no customer data found
            if (apiData.length === 0) {
                setShowNoCustomerFoundMessage(true);
                setCustomerOptions([]);
                clearMonumentSelection();
                return;
            }

            setShowNoCustomerFoundMessage(false);

            // Monument: the write-back note is account-level, so keep the DISTINCT tenants
            // separate (each with its own AccountUuid) rather than merging them into
            // record[0] like the SSM path below — otherwise a name matching >1 Monument
            // account would silently attach the note to the first. The agent picks the
            // account via MonumentTenantPicker; a single match is auto-selected. There are no
            // SSM unit/type cards for Monument, so clear that state.
            if (isMonument) {
                const { tenants, leads } = getMonumentTargets(apiData);
                setMonumentTenants(tenants);
                setMonumentLeads(leads);
                // Pre-select the first match so it's highlighted in the picker and drives the
                // note by default; the agent can click another card to override. Tenancies come
                // first, so a caller who is both a tenant and a prospect defaults to the tenancy.
                setSelectedMonumentAccountUuid((tenants[0] || leads[0])?.accountUuid || "");
                setSelectedMonumentUnitLabel("");
                setCustomerOptions([]);
                setSelectedCustomer("");
                setSelectedCustomerRecord(null);
                setOriginalCustomerRecord(null);
                // Nothing selectable means no write target at all — count PROSPECTS too, or a
                // caller who matched only leads is told there is no data and pushed into the
                // new-customer form, creating a duplicate lead.
                setShowVideoNoDataMessage(tenants.length + leads.length === 0);
                return;
            }

            // SSM: merge all records into record[0]. This is safe ONLY because the SSM
            // write-back keys on globally-unique per-record numbers (InquiryNo /
            // ReservationNumber / LeaseNumber) that the agent explicitly selects — not on
            // record identity. Do NOT add an account/tenant-level write-back here without
            // keeping records distinct (see getMonumentTenants — Monument's note is
            // account-level, which is exactly why it takes the un-merged path above).
            const allInquiries = apiData.flatMap((record: any) => record.Inquiries || []);
            const allReservations = apiData.flatMap((record: any) => record.Reservations || []);
            const allRentals = apiData.flatMap((record: any) => record.Rentals || []);

            // Use first record for basic customer info
            const firstCustomer = apiData[0];
            const firstName = firstCustomer.FirstName || "";
            const lastName = firstCustomer.LastName || "";

            // Create a combined customer object with ALL data from ALL records
            const combinedCustomer = {
                ...firstCustomer,
                Inquiries: allInquiries,
                Reservations: allReservations,
                Rentals: allRentals
            };

            // Update Redux with combined data (store as single record array)
            dispatch(setFetchedCustomerData([combinedCustomer]));

            // Check if combined customer has any data
            const hasData = allInquiries.length > 0 || allReservations.length > 0 || allRentals.length > 0;

            if (!hasData) {
                // Customer found but has no data - show "no data" message
                setShowVideoNoDataMessage(true);
                setCustomerOptions([]);
                return;
            }

            // Process combined data to create unit-specific options with type
            const options: Array<{ value: string, label: string }> = [];
            const uniqueCombinations = new Set<string>();

            // Process all combined inquiries
            allInquiries.forEach((inquiry: any) => {
                const unitNumber = inquiry.UnitNumber || "N/A";
                const combination = `${firstName}-${lastName}-${unitNumber}-Inquiry`;

                if (!uniqueCombinations.has(combination)) {
                    uniqueCombinations.add(combination);
                    options.push({
                        value: combination,
                        label: `${firstName} ${lastName} - Unit ${unitNumber} - Inquiry`
                    });
                }
            });

            // Process all combined reservations
            allReservations.forEach((reservation: any) => {
                const unitNumber = reservation.UnitNumber || "N/A";
                const combination = `${firstName}-${lastName}-${unitNumber}-Reservation`;

                if (!uniqueCombinations.has(combination)) {
                    uniqueCombinations.add(combination);
                    options.push({
                        value: combination,
                        label: `${firstName} ${lastName} - Unit ${unitNumber} - Reservation`
                    });
                }
            });

            // Process all combined rentals
            allRentals.forEach((rental: any) => {
                const unitNumber = rental.UnitNumber || "N/A";
                const combination = `${firstName}-${lastName}-${unitNumber}-Rental`;

                if (!uniqueCombinations.has(combination)) {
                    uniqueCombinations.add(combination);
                    options.push({
                        value: combination,
                        label: `${firstName} ${lastName} - Unit ${unitNumber} - Rental`
                    });
                }
            });

            setCustomerOptions(options);
        } catch (err) {
            // The write-back target is deliberately left alone: a transport failure says
            // nothing about the customer. Report it instead — silently dropping the search
            // reads as "no match", which is a different answer.
            console.error("Fetch API Error:", err);
            setApiError("Failed to fetch customer details. Please try again.");
        } finally {
            setIsFetching(false);
        }
    };


    const handlePrimaryButtonClick = () => {
        const formElement = document.getElementById("video-customer-details-form") as HTMLFormElement;
        if (formElement) {
            formElement.requestSubmit();
        }
    };

    // Handle cancel button: Submit immediately if countdown is active (after call ends)
    const handleCancelClick = async () => {
        // During call (countdown = 0): Just close popup and mark as cancelled
        if (countdown === 0) {
            setIsCancelledDuringCall(true); // Track that user cancelled during call
            onClose(true); // Pass true to indicate cancellation during call
            return;
        }

        // After call ends (countdown > 0): Submit immediately then close
        // Double-submit prevention: Check if already submitted or submitting
        if (isSubmitted || isSubmitting) {
            onClose(false);
            return;
        }

        // Submit immediately
        setIsSubmitting(true);
        try {
            await handleTenantDetailsSubmit(undefined, true);
            // isSubmitted will be set to true in handleTenantDetailsSubmit on success
        } catch (error) {
            console.error("Submit on cancel failed (video):", error);
            // Still close even if submit fails
        } finally {
            setIsSubmitting(false);
            onClose(false);
        }
    };

    const handleTenantDetailsSubmit = async (e?: React.FormEvent, isAutoSubmit: boolean = false) => {
        if (e) e.preventDefault();

        // Prevent duplicate submissions
        if (isSubmitted || isSubmitting) return;

        // No write-back for this PMS — a submit here would POST a payload the write-back
        // router silently drops, then flip isSubmitted and close the modal, reading as a
        // false "saved". Never write when the capability says there is nowhere to write.
        if (isWriteBackSuppressed) return;

        // Clear previous errors
        setErrors({});
        setApiError("");

        const newErrors: { [key: string]: string } = {};

        // Get facilityId from fetchedCustomerData (now always a single combined record)
        const getfacilityId = () => {
            const customerRecord = fetchedCustomerData?.[0];
            if (customerRecord?.facilityId) {
                return customerRecord.facilityId;
            }
            return null;
        };

        // R4: the location's PMS is fetched asynchronously. Until that resolves (or if
        // every retry failed) we cannot tell Monument from SSM, and guessing would route
        // the write to the wrong PMS — an SSM-shaped payload for a Monument location is
        // silently dropped by the write-back router. Refuse rather than mis-submit. (The
        // fetch is fast and retried, so in practice this is resolved well before submit.)
        if (!providerLoaded || providerLoadError) {
            if (!isAutoSubmit) {
                if (providerLoadError) {
                    // Kick off a fresh detection attempt; the effect re-runs on the nonce.
                    setProviderRetryNonce((n) => n + 1);
                    setApiError("Re-checking this location's system — please submit again in a moment. If it keeps failing, record the summary manually and contact support.");
                } else {
                    setApiError("Still detecting this location's system. Please wait a moment and submit again.");
                }
            } else {
                console.warn("[Monument] video auto-submit skipped: location PMS not resolved; not guessing the provider.");
            }
            return;
        }

        // Monument writes leads/notes, not SSM records — its payload is a different,
        // smaller shape and skips the SSM classification/date/advSource machinery. The
        // SSM/HB path below stays untouched (this returns before reaching it).
        if (isMonument) {
            // Existing-tenant write-back targets the SELECTED Monument account (its own
            // AccountUuid), not a merged record[0]. selectedMonumentTenant defaults to the
            // first matched account (agent-overridable via the picker); it is only null when
            // the read returned no writable account, which the AccountUuid guard below catches.
            const customer = selectedMonumentTenant?.record;
            const agentName = LoginUserDetails?.name || "";
            // No caller number on a video call — the agent enters a phone and/or an email
            // (Monument's create-lead needs at least one).
            const phoneRaw = phoneNumber?.trim() || "";
            const phone = removeCountryCode(phoneRaw);
            // Auto-submit has no UI to report an error, so an unparseable phone must SKIP the
            // write: removeCountryCode collapses "wqewe" to "", which the gate reads as no phone.
            const phoneInvalid = !!phoneRaw && !validatePhoneNumber(phoneRaw).isValid;
            let payload: any;
            if (customerType === "new") {
                if (!isAutoSubmit) {
                    const monErrors = validateMonumentNewLead(
                        { firstName: tenantDetails.firstName, lastName: tenantDetails.lastName, phone: phoneRaw, email }
                    );
                    if (Object.keys(monErrors).length > 0) { setErrors(monErrors); return; }
                } else if (phoneInvalid || !monumentNewLeadCanAutoSubmit({ firstName: tenantDetails.firstName, lastName: tenantDetails.lastName, phone, email })) {
                    // B2: an auto-submit missing a Monument-required field would POST a
                    // payload the backend silently refuses (no lead, no note). Skip the
                    // write instead of reporting a false success — leave isSubmitted false.
                    console.warn("[Monument] video auto-submit skipped: new-lead is missing a required field (name / phone-or-email) or the phone entered is not a valid number.");
                    return;
                }
                payload = buildMonumentNewLeadPayload({
                    tenantDetails,
                    unitGroupUuid,
                    phone,
                    email,
                    callSid,
                    locationId,
                    agentName,
                    callType: "video",
                    leadSourceUuid,
                    addressLine1: addressLine1.trim(),
                    addressLine2: addressLine2.trim(),
                });
            } else {
                // B1: without a Monument AccountUuid the server-side write-back router
                // matches neither branch and the note is silently dropped. Video defaults
                // to the existing-customer tab, so this is the most likely place to hit it.
                // customer defaults to the first matched account, so this only fires when the
                // read returned NO writable Monument account (no AccountUuid at all) — nothing
                // to attach the note to.
                // A prospect has no AccountUuid by design, so the guard asks whether the
                // selection resolves to ANY write target — checking AccountUuid alone would
                // reject every lead and push the agent into creating a duplicate.
                if (!getMonumentTarget(customer).targetUuid) {
                    if (!isAutoSubmit) {
                        setApiError("Search for and select the tenant first — no Monument account is selected to attach this call summary to.");
                    } else {
                        console.warn("[Monument] video auto-submit skipped: existing customer has no Monument AccountUuid.");
                    }
                    return;
                }
                payload = buildMonumentExistingPayload(customer, callSid, locationId, agentName, "video", buildMonumentUnitLabel(selectedMonumentTenant, selectedMonumentUnitLabel));
            }
            setIsSubmitting(true);
            try {
                console.log(payload, 'Customer Payload (video Monument) ==============>');
                const res = await PostApiMethod("tenant/sendCallSummary/postCustomerDetails", payload);
                console.log("Video Monument Customer API Response:", res);
                setIsSubmitted(true);
                if (!isAutoSubmit) onClose(false);
            } catch (err) {
                console.error("Video Monument Customer API Error:", err);
                setApiError(err instanceof Error ? err.message : "Failed to submit customer details. Please try again.");
            } finally {
                setIsSubmitting(false);
            }
            return;
        }

        if (customerType === "new") {
            // New customer - video call
            if (!isAutoSubmit) {
                if (!tenantDetails.firstName.trim()) newErrors.firstName = "First Name is required";
                if (!tenantDetails.lastName.trim()) newErrors.lastName = "Last Name is required";
                if (!dateNeeded) newErrors.dateNeeded = "Date Needed is required";
                if (!tenantDetails.advSource) newErrors.advSource = "Please select how you heard about us";

                setErrors(newErrors);
                if (Object.keys(newErrors).length > 0) return;
            }

            const newCustomerPayload = {
                tenantId: tenantDetails.tenantId.trim() || 0,
                firstName: tenantDetails.firstName.trim(),
                lastName: tenantDetails.lastName.trim(),
                facilityId: getfacilityId(),
                countryId: tenantDetails.countryId,
                dateNeeded: dateNeeded ? dateNeeded.toISOString().split("T")[0] : tenantDetails.dateNeeded,
                advSource: tenantDetails.advSource,
                customerType: "new",
                callType: "video",
                callSid: callSid,
                locationId: locationId,
                agentName: LoginUserDetails?.name || "",
            };

            setIsSubmitting(true);
            try {
                console.log(newCustomerPayload, 'newCustomerPayload video ==============>');
                const res = await PostApiMethod("tenant/sendCallSummary/postCustomerDetails", newCustomerPayload);
                console.log("New Customer API Response:", res);
                setIsSubmitted(true);
                if (!isAutoSubmit) onClose(false);
            } catch (err) {
                console.error("New Customer API Error:", err);
                setApiError(err instanceof Error ? err.message : "Failed to submit customer details. Please try again.");
            } finally {
                setIsSubmitting(false);
            }
        } else {
            // Existing customer - Video call
            if (!isAutoSubmit) {
                if (!tenantDetails.firstName.trim()) newErrors.firstName = "First Name is required";
                if (!tenantDetails.lastName.trim()) newErrors.lastName = "Last Name is required";

                setErrors(newErrors);
                if (Object.keys(newErrors).length > 0) return;
            }

            // Use the appropriate customer data source
            let customer = selectedCustomerRecord || originalCustomerRecord;

            // If no customer card selected but we have fetched data, use the combined record
            if (!customer && fetchedCustomerData?.length > 0) {
                // fetchedCustomerData[0] already contains all combined data from all records
                customer = fetchedCustomerData[0];
            }

            // Smart default logic for classification and details
            let finalClassification = tenantDetails.classification;
            let finalInquiryNo = tenantDetails.inquiryDetails;
            let finalReservationNumber = tenantDetails.reservationDetails;
            let finalLeaseNumber = tenantDetails.rentalDetails;

            // If no classification selected, use priority: Inquiry > Reservation > Rental
            if (!finalClassification) {
                if (customer?.Inquiries?.length > 0) {
                    finalClassification = "Inquiry";
                } else if (customer?.Reservations?.length > 0) {
                    finalClassification = "Reservation";
                } else if (customer?.Rentals?.length > 0) {
                    finalClassification = "Rental";
                }
            }

            // Apply default first record logic for each classification
            if (finalClassification === "Inquiry") {
                // If no inquiry selected, use first inquiry record
                if (!finalInquiryNo && customer?.Inquiries?.length > 0) {
                    finalInquiryNo = customer.Inquiries[0].InquiryNumber;
                }
            } else if (finalClassification === "Reservation") {
                // If no reservation selected, use first reservation record
                if (!finalReservationNumber && customer?.Reservations?.length > 0) {
                    finalReservationNumber = customer.Reservations[0].ReservationNumber;
                }
            } else if (finalClassification === "Rental") {
                // If no rental selected, use first rental record
                if (!finalLeaseNumber && customer?.Rentals?.length > 0) {
                    finalLeaseNumber = customer.Rentals[0].LeaseNumber;
                }
            }

            let videoPayload;

            // Get customer names from form or fetched data
            const customerFirstName = tenantDetails.firstName.trim() || customer?.FirstName || "";
            const customerLastName = tenantDetails.lastName.trim() || customer?.LastName || "";

            // Build payload based on final classification with specific field order
            if (finalClassification === "Inquiry") {
                videoPayload = {
                    facilityId: getfacilityId(),
                    InquiryNo: finalInquiryNo,
                    classification: finalClassification,
                    customerType: "existing",
                    callType: "video",
                    locationId: locationId,
                    callSid: callSid,
                    agentName: LoginUserDetails?.name || "",
                    customerFirstName: customerFirstName,
                    customerLastName: customerLastName
                };
            } else if (finalClassification === "Reservation") {
                videoPayload = {
                    facilityId: getfacilityId(),
                    ReservationNumber: finalReservationNumber,
                    classification: finalClassification,
                    customerType: "existing",
                    callType: "video",
                    locationId: locationId,
                    callSid: callSid,
                    agentName: LoginUserDetails?.name || "",
                    customerFirstName: customerFirstName,
                    customerLastName: customerLastName
                };
            } else if (finalClassification === "Rental") {
                // Find the selected rental to get its TenantId (not the top-level customer TenantID)
                const selectedRental = customer?.Rentals?.find((rental: any) => rental.LeaseNumber === finalLeaseNumber);

                videoPayload = {
                    TenantId: selectedRental?.TenantId?.toString() || customer?.TenantID?.toString() || "",
                    LeaseNumber: finalLeaseNumber,
                    facilityId: getfacilityId(),
                    classification: finalClassification,
                    customerType: "existing",
                    callType: "video",
                    locationId: locationId,
                    callSid: callSid,
                    agentName: LoginUserDetails?.name || "",
                    customerFirstName: customerFirstName,
                    customerLastName: customerLastName
                };
            } else {
                videoPayload = {
                    classification: finalClassification,
                    customerType: "existing",
                    callType: "video",
                    locationId: locationId,
                    callSid: callSid,
                    facilityId: getfacilityId(),
                    agentName: LoginUserDetails?.name || "",
                    customerFirstName: customerFirstName,
                    customerLastName: customerLastName
                };
            }

            setIsSubmitting(true);
            try {
                console.log(videoPayload, 'videoPayload ==============>');
                const res = await PostApiMethod("tenant/sendCallSummary/postCustomerDetails", videoPayload);
                console.log("Video Existing Customer API Response:", res);
                setIsSubmitted(true);
                if (!isAutoSubmit) onClose(false);
            } catch (err) {
                console.error("Video Existing Customer API Error:", err);
                setApiError(err instanceof Error ? err.message : "Failed to submit customer details. Please try again.");
            } finally {
                setIsSubmitting(false);
            }
        }
    };

    // Expose autoSubmit function and isSubmitted state via ref
    React.useImperativeHandle(ref, () => ({
        autoSubmit: async () => {
            // The 60s call-end auto-submit fires via this ref regardless of whether the
            // modal is visible, so a suppressed PMS must be a no-op here too.
            if (isWriteBackSuppressed) return;
            if (!isSubmitted) {
                await handleTenantDetailsSubmit(undefined, true);
            }
        },
        getIsSubmitted: () => isSubmitted,
        getIsCancelledDuringCall: () => isCancelledDuringCall,
        resetCancelledDuringCall: () => setIsCancelledDuringCall(false),
        // Lets Dashboard short-circuit the call-end open + 60s countdown/auto-submit for a
        // suppressed location instead of running that machinery invisibly.
        getIsSuppressed: () => isWriteBackSuppressed
    }));

    if (!isOpen) return null;
    // Suppress the popup when the PMS has no write-back: nothing to write, and for
    // Hummingbird nothing to look up either (no name search + no phone on a video call). The
    // submit/autoSubmit guards above are the safety net for the call-end auto-submit ref;
    // this keeps the popup itself from
    // ever appearing. isWriteBackSuppressed is false until the fetch resolves, so this only
    // triggers once we positively know the location has no write-back.
    if (isWriteBackSuppressed) return null;
    // While a provider fetch is genuinely in flight (we have a locationId but it hasn't
    // resolved yet) render nothing, rather than briefly flashing the SSM data-entry form and
    // then vanishing when it resolves to a suppressed PMS. Scope this to `locationId &&` on purpose:
    // with no locationId the fetch never runs and providerLoaded stays false forever, so an
    // unconditional guard would hide the modal permanently — instead fall through to the prior
    // behaviour. Submit is already gated on providerLoaded, so nothing is lost by waiting.
    // (providerLoaded flips true on success OR terminal failure, so this window is bounded.)
    if (locationId && !providerLoaded) return null;

    return (
        <AppModal
            title="Customer Details"
            bodyText=""
            primaryButtonLabel={isSubmitting ? "Submitting..." : "Submit"}
            secondaryButtonLabel="Cancel"
            primaryButtonEventEmitter={handlePrimaryButtonClick}
            secondaryButtonEventEmitter={handleCancelClick}
            stayAppear={true}
            // Both messages render only in existing-customer mode, so they must only gate
            // Submit there. Gating unconditionally left the new-customer form fillable but
            // unsubmittable whenever a search had latched either flag.
            disablePrimaryButton={(customerType === "existing" && (showVideoNoDataMessage || showNoCustomerFoundMessage)) || isSubmitting}
        >
            <form id="video-customer-details-form" onSubmit={handleTenantDetailsSubmit} style={{ position: 'relative' }}>
                {/* Countdown Timer Message */}
                {countdown > 0 && (
                    <div className="alert alert-warning alert-dismissible fade show mb-3" role="alert" style={{ borderLeft: '4px solid #ff9800' }}>
                        <div className="d-flex align-items-center">
                            <i className="bi bi-clock-fill me-2" style={{ fontSize: '1.5rem' }}></i>
                            <div>
                                <strong>Auto-submitting in {countdown} second{countdown !== 1 ? 's' : ''}...</strong>
                                <br />
                                <small className="text-muted">Fill in the details or submit now to avoid auto-submission</small>
                            </div>
                        </div>
                    </div>
                )}

                {/* API Error Message */}
                {apiError && (
                    <div className="alert alert-danger alert-dismissible fade show" role="alert">
                        <i className="bi bi-exclamation-triangle-fill me-2"></i>
                        <strong>Error:</strong> {apiError}
                        <button
                            type="button"
                            className="btn-close"
                            onClick={() => setApiError("")}
                            aria-label="Close"
                        ></button>
                    </div>
                )}

                {/* Elegant Loading Overlay - Only for Modal */}
                {isSubmitting && (
                    <div
                        className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                        style={{
                            backgroundColor: 'rgba(0, 0, 0, 0.4)',
                            zIndex: 1050,
                            backdropFilter: 'blur(4px)',
                            borderRadius: '0.5rem'
                        }}
                    >
                        <div className="text-center">
                            <div className="spinner-border text-light" style={{ width: '3rem', height: '3rem', borderWidth: '0.3rem' }} role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <div className="mt-3 text-white fw-medium">Processing...</div>
                        </div>
                    </div>
                )}

                {/* Beautiful Header with Toggle Button */}
                <div className="mb-4">
                    {customerType === "existing" ? (
                        <div className="bg-gradient-primary rounded-3 p-4 text-white position-relative" style={{ background: 'linear-gradient(135deg, #007bff 0%, #0056b3 100%)' }}>
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <div className="d-flex align-items-center">
                                        {/* <div className="bg-white bg-opacity-20 rounded-circle p-2 me-3">
                                            <i className="bi bi-search" style={{ fontSize: '1.2rem' }}></i>
                                        </div> */}
                                        <h5 className="mb-0 fw-bold">Find Existing Customer</h5>
                                    </div>
                                    <small className="mb-0 opacity-90">Search by customer name to find existing records</small>
                                </div>
                                <button
                                    type="button"
                                    className="btn btn-light btn-sm d-flex align-items-center shadow-sm"
                                    onClick={() => {
                                        setCustomerType("new");
                                        setShowVideoNoDataMessage(false);
                                    }}
                                    title="Register New Customer"
                                >
                                    <i className="bi bi-person-plus me-1"></i>
                                    New Customer
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="bg-gradient-success rounded-3 p-4 text-white position-relative" style={{ background: 'linear-gradient(135deg, #28a745 0%, #1e7e34 100%)' }}>
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <div className="d-flex align-items-center mb-2">
                                        {/* <div className="bg-white bg-opacity-20 rounded-circle p-2 me-3">
                                            <i className="bi bi-person-plus" style={{ fontSize: '1.2rem' }}></i>
                                        </div> */}
                                        <h5 className="mb-0 fw-bold">New Customer Registration</h5>
                                    </div>
                                    <small className="mb-0 opacity-90">Fill in the details to register a new customer</small>
                                </div>
                                <button
                                    type="button"
                                    className="btn btn-light btn-sm d-flex align-items-center shadow-sm"
                                    onClick={() => {
                                        setCustomerType("existing");
                                        setShowVideoNoDataMessage(false);
                                    }}
                                    title="Search Existing Customer"
                                >
                                    <i className="bi bi-search me-1"></i>
                                    Find Existing
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Video Call - Existing Customer - Only First Name and Last Name */}
                {customerType === "existing" && (
                    <>
                        <div className="row mb-3">
                            <div className="col-5">
                                <label className="form-label fw-semibold bg-light px-2 py-1 rounded">
                                    First Name <span className="text-danger">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="firstName"
                                    className="form-control"
                                    value={tenantDetails.firstName}
                                    onChange={(e) => setTenantDetails({ ...tenantDetails, firstName: e.target.value })}
                                />
                                {errors.firstName && <small className="text-danger">{errors.firstName}</small>}
                            </div>
                            <div className="col-5">
                                <label className="form-label fw-semibold bg-light px-2 py-1 rounded">
                                    Last Name <span className="text-danger">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="lastName"
                                    className="form-control"
                                    value={tenantDetails.lastName}
                                    onChange={(e) => setTenantDetails({ ...tenantDetails, lastName: e.target.value })}
                                />
                                {errors.lastName && <small className="text-danger">{errors.lastName}</small>}
                            </div>
                            <div className="col-2 text-center" style={{ paddingTop: "40px" }}>
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={handleFetchCustomer}
                                    disabled={isFetching}
                                >
                                    {isFetching ? "Fetching..." : "Fetch"}
                                </button>
                            </div>
                        </div>

                        {/* No customer found message */}
                        {showNoCustomerFoundMessage && (
                            <div className="row mb-3 justify-content-center">
                                <div className="col-lg-10">
                                    <div className="card border-danger shadow-sm">
                                        <div className="card-body text-center p-3">
                                            <div className="mb-2">
                                                <i className="bi bi-search text-danger" style={{ fontSize: '2.5rem' }}></i>
                                            </div>
                                            <h6 className="card-title text-danger mb-2">No Customer Found</h6>
                                            <p className="card-text text-muted mb-3">
                                                No customer found with the provided name.
                                                <br />
                                                <small>Please verify the spelling or proceed with the new customer process.</small>
                                            </p>
                                            <button
                                                type="button"
                                                className="btn btn-primary px-3"
                                                onClick={() => {
                                                    setCustomerType("new");
                                                    setShowNoCustomerFoundMessage(false);
                                                }}
                                            >
                                                <i className="bi bi-person-plus me-2"></i>
                                                Create New Customer
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Customer Cards — SSM only. Monument uses the account-level tenant
                            picker below (its note is not keyed on a per-unit record). */}
                        {!isMonument && customerOptions.length > 0 && (
                            <div className="row mb-3">
                                <div className="col-12">
                                    <label className="form-label fw-bold bg-light px-3 py-2 rounded mb-3 d-inline-flex align-items-center">
                                        <i className="bi bi-people text-primary me-2"></i>
                                        Select Customer
                                    </label>
                                    <div className="row g-3">
                                        {customerOptions.map((option, index) => {
                                            const parts = option.value.split('-');
                                            const firstName = parts[0];
                                            const lastName = parts[1];
                                            const unitNumber = parts[2];
                                            const recordType = parts[3];
                                            const isSelected = selectedCustomer === option.value;

                                            return (
                                                <div key={index} className="col-md-6 col-lg-3">
                                                    <div
                                                        className={`card h-100 ${isSelected ? 'border-2 border-primary bg-primary bg-opacity-10 shadow' : 'border border-secondary'}`}
                                                        style={{
                                                            cursor: 'pointer',
                                                            transition: 'all 0.2s ease-in-out',
                                                            transform: isSelected ? 'scale(1.02)' : 'scale(1)'
                                                        }}
                                                        onMouseEnter={(e) => {
                                                            if (!isSelected) {
                                                                e.currentTarget.style.transform = 'translateY(-2px)';
                                                                e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.1)';
                                                            }
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            if (!isSelected) {
                                                                e.currentTarget.style.transform = 'translateY(0)';
                                                                e.currentTarget.style.boxShadow = '';
                                                            }
                                                        }}
                                                        onClick={() => {
                                                            // Toggle logic: if already selected, unselect it
                                                            if (isSelected) {
                                                                setSelectedCustomer("");
                                                                setSelectedCustomerRecord(null);
                                                                setOriginalCustomerRecord(null);
                                                                setTenantDetails({ ...tenantDetails, classification: "" });
                                                                return;
                                                            }

                                                            setSelectedCustomer(option.value);

                                                            let matchingRecords = fetchedCustomerData.filter((record: any) =>
                                                                record.FirstName === firstName && record.LastName === lastName
                                                            );

                                                            const combinedRecord = matchingRecords.length > 0 ? {
                                                                ...matchingRecords[0],
                                                                Rentals: matchingRecords.flatMap((record: any) => record.Rentals || []),
                                                                Inquiries: matchingRecords.flatMap((record: any) => record.Inquiries || []),
                                                                Reservations: matchingRecords.flatMap((record: any) => record.Reservations || [])
                                                            } : null;

                                                            if (!combinedRecord) {
                                                                setShowVideoNoDataMessage(true);
                                                                setOriginalCustomerRecord(null);
                                                                setSelectedCustomerRecord(null);
                                                                return;
                                                            }

                                                            setShowVideoNoDataMessage(false);
                                                            setOriginalCustomerRecord(combinedRecord);

                                                            let filteredRecord = { ...combinedRecord };

                                                            if (recordType === 'Inquiry') {
                                                                filteredRecord.Inquiries = combinedRecord.Inquiries?.filter((inquiry: any) =>
                                                                    String(inquiry.UnitNumber) === String(unitNumber)
                                                                ) || [];
                                                                filteredRecord.Rentals = [];
                                                                filteredRecord.Reservations = [];
                                                            } else if (recordType === 'Reservation') {
                                                                filteredRecord.Reservations = combinedRecord.Reservations?.filter((reservation: any) =>
                                                                    String(reservation.UnitNumber) === String(unitNumber)
                                                                ) || [];
                                                                filteredRecord.Inquiries = [];
                                                                filteredRecord.Rentals = [];
                                                            } else if (recordType === 'Rental') {
                                                                filteredRecord.Rentals = combinedRecord.Rentals?.filter((rental: any) =>
                                                                    String(rental.UnitNumber) === String(unitNumber)
                                                                ) || [];
                                                                filteredRecord.Inquiries = [];
                                                                filteredRecord.Reservations = [];
                                                            }

                                                            setSelectedCustomerRecord(filteredRecord);

                                                            if (recordType === 'Inquiry' || recordType === 'Reservation' || recordType === 'Rental') {
                                                                setTenantDetails({ ...tenantDetails, classification: recordType });
                                                            } else {
                                                                setTenantDetails({ ...tenantDetails, classification: "" });
                                                            }
                                                        }}
                                                    >
                                                        {isSelected && (
                                                            <div className="position-absolute top-0 end-0 p-1">
                                                                <i className="bi bi-check-circle-fill text-primary bg-white rounded-circle" style={{ fontSize: '1.1rem' }}></i>
                                                            </div>
                                                        )}
                                                        <div className="card-body p-2 text-center">
                                                            <div className="mb-1">
                                                                <i className={`bi bi-person-circle ${isSelected ? 'text-primary' : 'text-secondary'}`} style={{ fontSize: '1.5rem' }}></i>
                                                            </div>
                                                            <h6 className={`card-title mb-1 ${isSelected ? 'fw-bold' : ''}`} style={{ fontSize: '0.85rem' }}>
                                                                {firstName} {lastName}
                                                            </h6>
                                                            <p className="card-text mb-1 text-muted" style={{ fontSize: '0.75rem' }}>
                                                                <i className="bi bi-geo-alt me-1"></i>
                                                                {unitNumber}
                                                            </p>
                                                            <span className={`badge ${recordType === 'Inquiry' ? 'bg-info' : recordType === 'Reservation' ? 'bg-warning text-dark' : recordType === 'Rental' ? 'bg-success' : 'bg-secondary'}`} style={{ fontSize: '0.65rem', padding: '0.25em 0.5em' }}>
                                                                {recordType}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* No data message for video existing customers */}
                        {showVideoNoDataMessage && (
                            <div className="row mb-3 justify-content-center">
                                <div className="col-lg-10">
                                    <div className="card border-warning shadow-sm">
                                        <div className="card-body text-center p-3">
                                            <div className="mb-2">
                                                <i className="bi bi-exclamation-triangle-fill text-warning" style={{ fontSize: '2.5rem' }}></i>
                                            </div>
                                            <h6 className="card-title text-warning mb-2">No Customer Data Found</h6>
                                            <p className="card-text text-muted mb-3">
                                                This customer doesn't have any inquiry, reservation, or rental records.
                                                <br />
                                                <small>You can proceed with the new customer process using their existing details.</small>
                                            </p>
                                            <button
                                                type="button"
                                                className="btn btn-primary px-3"
                                                onClick={() => {
                                                    setCustomerType("new");
                                                    setShowVideoNoDataMessage(false);
                                                    const customerRecord = fetchedCustomerData?.[0];
                                                    if (customerRecord?.TenantID) {
                                                        setTenantDetails({
                                                            ...tenantDetails,
                                                            tenantId: customerRecord.TenantID.toString()
                                                        });
                                                    }
                                                }}
                                            >
                                                <i className="bi bi-person-plus me-2"></i>
                                                Continue as New Customer
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Call Purpose - Interactive Cards */}
                        {/* {originalCustomerRecord && !tenantDetails.classification && (
                            <div className="row mb-3">
                                <div className="col-12">
                                    <label className="form-label fw-bold bg-light px-2 py-1 rounded mb-3">What can we help you with today?</label>
                                    <div className="row g-3">
                                        {originalCustomerRecord.Inquiries?.length > 0 && (
                                            <div className="col-md-4">
                                                <div
                                                    className={`card h-100 cursor-pointer border-2 ${tenantDetails.classification === 'Inquiry' ? 'border-info bg-info bg-opacity-10' : 'border-light'} hover-shadow`}
                                                    style={{ cursor: 'pointer', transition: 'all 0.3s ease' }}
                                                    onClick={() => setTenantDetails({ ...tenantDetails, classification: 'Inquiry' })}
                                                    onMouseEnter={(e) => {
                                                        if (tenantDetails.classification !== 'Inquiry') {
                                                            e.currentTarget.style.transform = 'translateY(-2px)';
                                                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,123,255,0.15)';
                                                        }
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        if (tenantDetails.classification !== 'Inquiry') {
                                                            e.currentTarget.style.transform = 'translateY(0)';
                                                            e.currentTarget.style.boxShadow = '';
                                                        }
                                                    }}
                                                >
                                                    <div className="card-body text-center p-3">
                                                        <div className={`mb-2 ${tenantDetails.classification === 'Inquiry' ? 'text-info' : 'text-muted'}`}>
                                                            <i className="bi bi-question-circle-fill" style={{ fontSize: '2.5rem' }}></i>
                                                        </div>
                                                        <h6 className={`card-title mb-2 ${tenantDetails.classification === 'Inquiry' ? 'text-info fw-bold' : 'text-dark'}`}>
                                                            Inquiry
                                                        </h6>
                                                        <p className="card-text text-muted small mb-0">
                                                            Questions about units, pricing, or availability
                                                        </p>
                                                        {tenantDetails.classification === 'Inquiry' && (
                                                            <div className="mt-2">
                                                                <i className="bi bi-check-circle-fill text-info"></i>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        {originalCustomerRecord.Reservations?.length > 0 && (
                                            <div className="col-md-4">
                                                <div
                                                    className={`card h-100 cursor-pointer border-2 ${tenantDetails.classification === 'Reservation' ? 'border-warning bg-warning bg-opacity-10' : 'border-light'} hover-shadow`}
                                                    style={{ cursor: 'pointer', transition: 'all 0.3s ease' }}
                                                    onClick={() => setTenantDetails({ ...tenantDetails, classification: 'Reservation' })}
                                                    onMouseEnter={(e) => {
                                                        if (tenantDetails.classification !== 'Reservation') {
                                                            e.currentTarget.style.transform = 'translateY(-2px)';
                                                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(255,193,7,0.15)';
                                                        }
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        if (tenantDetails.classification !== 'Reservation') {
                                                            e.currentTarget.style.transform = 'translateY(0)';
                                                            e.currentTarget.style.boxShadow = '';
                                                        }
                                                    }}
                                                >
                                                    <div className="card-body text-center p-3">
                                                        <div className={`mb-2 ${tenantDetails.classification === 'Reservation' ? 'text-warning' : 'text-muted'}`}>
                                                            <i className="bi bi-calendar-check-fill" style={{ fontSize: '2.5rem' }}></i>
                                                        </div>
                                                        <h6 className={`card-title mb-2 ${tenantDetails.classification === 'Reservation' ? 'text-warning fw-bold' : 'text-dark'}`}>
                                                            Reservation
                                                        </h6>
                                                        <p className="card-text text-muted small mb-0">
                                                            Manage existing reservations or bookings
                                                        </p>
                                                        {tenantDetails.classification === 'Reservation' && (
                                                            <div className="mt-2">
                                                                <i className="bi bi-check-circle-fill text-warning"></i>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        {originalCustomerRecord.Rentals?.length > 0 && (
                                            <div className="col-md-4">
                                                <div
                                                    className={`card h-100 cursor-pointer border-2 ${tenantDetails.classification === 'Rental' ? 'border-success bg-success bg-opacity-10' : 'border-light'} hover-shadow`}
                                                    style={{ cursor: 'pointer', transition: 'all 0.3s ease' }}
                                                    onClick={() => setTenantDetails({ ...tenantDetails, classification: 'Rental' })}
                                                    onMouseEnter={(e) => {
                                                        if (tenantDetails.classification !== 'Rental') {
                                                            e.currentTarget.style.transform = 'translateY(-2px)';
                                                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(40,167,69,0.15)';
                                                        }
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        if (tenantDetails.classification !== 'Rental') {
                                                            e.currentTarget.style.transform = 'translateY(0)';
                                                            e.currentTarget.style.boxShadow = '';
                                                        }
                                                    }}
                                                >
                                                    <div className="card-body text-center p-3">
                                                        <div className={`mb-2 ${tenantDetails.classification === 'Rental' ? 'text-success' : 'text-muted'}`}>
                                                            <i className="bi bi-house-fill" style={{ fontSize: '2.5rem' }}></i>
                                                        </div>
                                                        <h6 className={`card-title mb-2 ${tenantDetails.classification === 'Rental' ? 'text-success fw-bold' : 'text-dark'}`}>
                                                            Rental
                                                        </h6>
                                                        <p className="card-text text-muted small mb-0">
                                                            Support for current rental agreements
                                                        </p>
                                                        {tenantDetails.classification === 'Rental' && (
                                                            <div className="mt-2">
                                                                <i className="bi bi-check-circle-fill text-success"></i>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )} */}

                        {/* Inquiry Details — SSM only (Monument writes a note, not a record) */}
                        {!isMonument && selectedCustomerRecord && tenantDetails.classification === "Inquiry" && (
                            <div className="row mb-3">
                                <div className="col-12">
                                    <label className="form-label fw-bold bg-light px-3 py-2 rounded mb-3 d-inline-flex align-items-center">
                                        <i className="bi bi-question-circle text-info me-2"></i>
                                        Select Inquiry
                                    </label>
                                    {selectedCustomerRecord.Inquiries?.length > 0 ? (
                                        <div className="d-flex flex-wrap gap-2">
                                            {selectedCustomerRecord.Inquiries.map((inquiry: any, index: number) => {
                                                const isSelected = tenantDetails.inquiryDetails === inquiry.InquiryNumber;
                                                return (
                                                    <div
                                                        key={index}
                                                        className={`px-3 py-2 rounded-pill ${isSelected ? 'bg-info text-white' : 'bg-light border'}`}
                                                        style={{
                                                            cursor: 'pointer',
                                                            transition: 'all 0.2s ease',
                                                            fontSize: '0.9rem'
                                                        }}
                                                        onClick={() => setTenantDetails({
                                                            ...tenantDetails,
                                                            inquiryDetails: isSelected ? "" : inquiry.InquiryNumber
                                                        })}
                                                        onMouseEnter={(e) => {
                                                            if (!isSelected) {
                                                                e.currentTarget.style.backgroundColor = '#e9ecef';
                                                                e.currentTarget.style.transform = 'translateY(-2px)';
                                                            }
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            if (!isSelected) {
                                                                e.currentTarget.style.backgroundColor = '';
                                                                e.currentTarget.style.transform = 'translateY(0)';
                                                            }
                                                        }}
                                                    >
                                                        <i className="bi bi-question-circle-fill me-2"></i>
                                                        <strong>{inquiry.InquiryNumber || 'Inquiry'}</strong>
                                                        <span className="mx-2">•</span>
                                                        <i className="bi bi-geo-alt me-1"></i>
                                                        Unit {inquiry.UnitNumber || 'N/A'}
                                                        {isSelected && <i className="bi bi-check-circle-fill ms-2"></i>}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        <div className="text-center py-3">
                                            <i className="bi bi-inbox text-muted" style={{ fontSize: '2.5rem', opacity: 0.4 }}></i>
                                            <p className="text-muted mb-0 mt-2" style={{ fontSize: '0.85rem' }}>No inquiries available</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Reservation Details — SSM only */}
                        {!isMonument && selectedCustomerRecord && tenantDetails.classification === "Reservation" && (
                            <div className="row mb-3">
                                <div className="col-12">
                                    <label className="form-label fw-bold bg-light px-3 py-2 rounded mb-3 d-inline-flex align-items-center">
                                        <i className="bi bi-calendar-check text-warning me-2"></i>
                                        Select Reservation
                                    </label>
                                    {selectedCustomerRecord.Reservations?.length > 0 ? (
                                        <div className="d-flex flex-wrap gap-2">
                                            {selectedCustomerRecord.Reservations.map((reservation: any, index: number) => {
                                                const isSelected = tenantDetails.reservationDetails === reservation.ReservationNumber;
                                                return (
                                                    <div
                                                        key={index}
                                                        className={`px-3 py-2 rounded-pill ${isSelected ? 'bg-warning text-dark' : 'bg-light border'}`}
                                                        style={{
                                                            cursor: 'pointer',
                                                            transition: 'all 0.2s ease',
                                                            fontSize: '0.9rem'
                                                        }}
                                                        onClick={() => setTenantDetails({
                                                            ...tenantDetails,
                                                            reservationDetails: isSelected ? "" : reservation.ReservationNumber
                                                        })}
                                                        onMouseEnter={(e) => {
                                                            if (!isSelected) {
                                                                e.currentTarget.style.backgroundColor = '#e9ecef';
                                                                e.currentTarget.style.transform = 'translateY(-2px)';
                                                            }
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            if (!isSelected) {
                                                                e.currentTarget.style.backgroundColor = '';
                                                                e.currentTarget.style.transform = 'translateY(0)';
                                                            }
                                                        }}
                                                    >
                                                        <i className="bi bi-calendar-check-fill me-2"></i>
                                                        <strong>{reservation.ReservationNumber}</strong>
                                                        <span className="mx-2">•</span>
                                                        <i className="bi bi-geo-alt me-1"></i>
                                                        Unit {reservation.UnitNumber || 'N/A'}
                                                        {isSelected && <i className="bi bi-check-circle-fill ms-2"></i>}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        <div className="text-center py-3">
                                            <i className="bi bi-calendar-x text-muted" style={{ fontSize: '2.5rem', opacity: 0.4 }}></i>
                                            <p className="text-muted mb-0 mt-2" style={{ fontSize: '0.85rem' }}>No reservations available</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Rental Details — SSM only */}
                        {!isMonument && selectedCustomerRecord && tenantDetails.classification === "Rental" && (
                            <div className="row mb-3">
                                <div className="col-12">
                                    <label className="form-label fw-bold bg-light px-3 py-2 rounded mb-3 d-inline-flex align-items-center">
                                        <i className="bi bi-house text-success me-2"></i>
                                        Select Rental
                                    </label>
                                    {selectedCustomerRecord.Rentals?.length > 0 ? (
                                        <div className="d-flex flex-wrap gap-2">
                                            {selectedCustomerRecord.Rentals.map((rental: any, index: number) => {
                                                const isSelected = tenantDetails.rentalDetails === rental.LeaseNumber;
                                                return (
                                                    <div
                                                        key={index}
                                                        className={`px-3 py-2 rounded-pill ${isSelected ? 'bg-success text-white' : 'bg-light border'}`}
                                                        style={{
                                                            cursor: 'pointer',
                                                            transition: 'all 0.2s ease',
                                                            fontSize: '0.9rem'
                                                        }}
                                                        onClick={() => setTenantDetails({
                                                            ...tenantDetails,
                                                            rentalDetails: isSelected ? "" : rental.LeaseNumber
                                                        })}
                                                        onMouseEnter={(e) => {
                                                            if (!isSelected) {
                                                                e.currentTarget.style.backgroundColor = '#e9ecef';
                                                                e.currentTarget.style.transform = 'translateY(-2px)';
                                                            }
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            if (!isSelected) {
                                                                e.currentTarget.style.backgroundColor = '';
                                                                e.currentTarget.style.transform = 'translateY(0)';
                                                            }
                                                        }}
                                                    >
                                                        <i className="bi bi-house-fill me-2"></i>
                                                        <strong>{rental.LeaseNumber || `REN${index + 1}`}</strong>
                                                        <span className="mx-2">•</span>
                                                        <i className="bi bi-geo-alt me-1"></i>
                                                        Unit {rental.UnitNumber || 'N/A'}
                                                        {isSelected && <i className="bi bi-check-circle-fill ms-2"></i>}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        <div className="text-center py-3">
                                            <i className="bi bi-house-x text-muted" style={{ fontSize: '2.5rem', opacity: 0.4 }}></i>
                                            <p className="text-muted mb-0 mt-2" style={{ fontSize: '0.85rem' }}>No rentals available</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Monument existing tenant — no SSM classification/records; the note
                            is the auto-generated call summary. When the search matched more
                            than one Monument account the agent picks which one it attaches to;
                            a single match is auto-selected. */}
                        {isMonument && (() => {
                            // Exactly what the note will be stamped with, so the agent sees it before submitting.
                            const monumentUnitStamp = buildMonumentUnitLabel(selectedMonumentTenant, selectedMonumentUnitLabel);
                            return (
                                <>
                                    <MonumentTenantPicker
                                        tenants={monumentTenants}
                                        leads={monumentLeads}
                                        selectedAccountUuid={selectedMonumentAccountUuid}
                                        onSelect={(uuid) => { setSelectedMonumentAccountUuid(uuid); setSelectedMonumentUnitLabel(""); }}
                                        selectedUnitLabel={selectedMonumentUnitLabel}
                                        onSelectUnit={setSelectedMonumentUnitLabel}
                                        disabled={isSubmitting}
                                    />
                                    <div
                                        className="d-flex align-items-start gap-2 px-3 py-2 mb-3 rounded-3"
                                        style={{ backgroundColor: '#eef5ff', border: '1px solid #cfe2ff' }}
                                        role="note"
                                    >
                                        <i
                                            className="bi bi-info-circle-fill"
                                            style={{ color: '#0d6efd', fontSize: '1rem', marginTop: '2px', flexShrink: 0 }}
                                        ></i>
                                        <div style={{ fontSize: '0.8rem', lineHeight: 1.4, color: '#334155' }}>
                                            {selectedMonumentTenant ? (
                                                <>
                                                    {selectedMonumentTenant.targetType === "lead"
                                                        ? "The call summary will be saved as a note on this lead in Monument."
                                                        : monumentUnitStamp
                                                            ? <>{"The call summary will be saved as a note on this tenant's Monument account, headed "}<span className="fw-semibold text-dark">{monumentUnitStamp}</span>{"."}</>
                                                            : "The call summary will be saved as a note on this tenant's Monument account."}
                                                </>
                                            ) : (
                                                "Search by name and select the tenant or lead — the call summary will be saved as a note on their Monument record."
                                            )}
                                        </div>
                                    </div>
                                </>
                            );
                        })()}
                    </>
                )}

                {/* Show all fields for new customers */}
                {customerType === "new" && (
                    <div className="mb-3">
                        {/* Tenant ID & Country ID — SSM only */}
                        {!isMonument && (
                        <div className="row g-3 mb-3">
                            <div className="col-md-6">
                                <div className="form-floating">
                                    <input
                                        type="text"
                                        name="tenantId"
                                        id="tenantId"
                                        className={`form-control ${errors.tenantId ? 'is-invalid' : ''}`}
                                        value={tenantDetails.tenantId}
                                        onChange={(e) => setTenantDetails({ ...tenantDetails, tenantId: e.target.value })}
                                        disabled={isSubmitting}
                                        placeholder="Tenant ID"
                                    />
                                    <label htmlFor="tenantId">
                                        <i className="bi bi-person-badge text-primary me-2"></i>
                                        Tenant ID
                                    </label>
                                    {errors.tenantId && <div className="invalid-feedback">{errors.tenantId}</div>}
                                </div>
                            </div>

                            <div className="col-md-6">
                                <CountryCodeDropdown
                                    value={tenantDetails.countryId}
                                    onChange={(countryId) => setTenantDetails({ ...tenantDetails, countryId: countryId.toString() })}
                                    disabled={isSubmitting}
                                />
                            </div>
                        </div>
                        )}

                        {/* First & Last Name */}
                        <div className="row g-3 mb-3">
                            <div className="col-md-6">
                                <div className="form-floating">
                                    <input
                                        type="text"
                                        name="firstName"
                                        id="firstName"
                                        className={`form-control ${errors.firstName ? 'is-invalid' : ''}`}
                                        value={tenantDetails.firstName}
                                        onChange={(e) => setTenantDetails({ ...tenantDetails, firstName: e.target.value })}
                                        disabled={isSubmitting}
                                        placeholder="First Name"
                                    />
                                    <label htmlFor="firstName">
                                        <i className="bi bi-person text-primary me-2"></i>
                                        First Name <span className="text-danger">*</span>
                                    </label>
                                    {errors.firstName && <div className="invalid-feedback">{errors.firstName}</div>}
                                </div>
                            </div>

                            <div className="col-md-6">
                                <div className="form-floating">
                                    <input
                                        type="text"
                                        name="lastName"
                                        id="lastName"
                                        className={`form-control ${errors.lastName ? 'is-invalid' : ''}`}
                                        value={tenantDetails.lastName}
                                        onChange={(e) => setTenantDetails({ ...tenantDetails, lastName: e.target.value })}
                                        disabled={isSubmitting}
                                        placeholder="Last Name"
                                    />
                                    <label htmlFor="lastName">
                                        <i className="bi bi-person text-primary me-2"></i>
                                        Last Name <span className="text-danger">*</span>
                                    </label>
                                    {errors.lastName && <div className="invalid-feedback">{errors.lastName}</div>}
                                </div>
                            </div>
                        </div>

                        {/* Date Needed & Advertising Source — SSM only */}
                        {!isMonument && (
                        <div className="row g-3 mb-3">
                            <div className="col-md-6">
                                <label htmlFor="dateNeeded" className="form-label text-muted small mb-2">
                                    <i className="bi bi-calendar-event text-primary me-1"></i>
                                    Date Needed <span className="text-danger">*</span>
                                </label>
                                <DatePicker
                                    id="dateNeeded"
                                    selected={dateNeeded}
                                    onChange={handleDateChange}
                                    dateFormat="yyyy-MM-dd"
                                    className={`form-control ${errors.dateNeeded ? 'is-invalid' : ''}`}
                                    placeholderText="Select date needed"
                                    minDate={new Date()}
                                    disabled={isSubmitting}
                                />
                                {errors.dateNeeded && <div className="invalid-feedback d-block">{errors.dateNeeded}</div>}
                            </div>
                            <div className="col-md-6">
                                <label htmlFor="advSource" className="form-label text-muted small mb-2">
                                    <i className="bi bi-megaphone text-primary me-1"></i>
                                    Advertising Source <span className="text-danger">*</span>
                                </label>
                                <select
                                    className={`form-select ${errors.advSource ? 'is-invalid' : ''}`}
                                    id="advSource"
                                    name="advSource"
                                    value={tenantDetails.advSource}
                                    onChange={(e) => setTenantDetails({ ...tenantDetails, advSource: e.target.value })}
                                    disabled={isSubmitting}
                                >
                                    <option value="">Select...</option>
                                    <option value="Phone">Phone</option>
                                    <option value="Website">Website</option>
                                    <option value="Kiosk">Kiosk</option>
                                    <option value="QR code">QR Code</option>
                                </select>
                                {errors.advSource && <div className="invalid-feedback">{errors.advSource}</div>}
                            </div>
                        </div>
                        )}

                        {/* Monument new-lead fields — one floating-label style throughout,
                            matching the First/Last Name inputs above. */}
                        {isMonument && (
                            <>
                                {/* Address — optional. Monument's docs mark these required; its API accepts a lead without them. */}
                                <div className="row g-3 mb-3">
                                    <div className="col-md-6">
                                        <div className="form-floating">
                                            <input
                                                type="text"
                                                id="addressLine1"
                                                className="form-control"
                                                value={addressLine1}
                                                onChange={(e) => setAddressLine1(e.target.value)}
                                                placeholder="Street address"
                                                disabled={isSubmitting}
                                            />
                                            <label htmlFor="addressLine1">
                                                <i className="bi bi-geo-alt text-primary me-2"></i>
                                                Address Line 1
                                            </label>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="form-floating">
                                            <input
                                                type="text"
                                                id="addressLine2"
                                                className="form-control"
                                                value={addressLine2}
                                                onChange={(e) => setAddressLine2(e.target.value)}
                                                placeholder="Apt, suite, unit, etc."
                                                disabled={isSubmitting}
                                            />
                                            <label htmlFor="addressLine2">
                                                <i className="bi bi-geo-alt text-primary me-2"></i>
                                                Address Line 2
                                            </label>
                                        </div>
                                    </div>
                                </div>

                                {/* Phone OR email — Monument's create-lead needs at least one. */}
                                <div className="row g-3 mb-1">
                                    <div className="col-md-6">
                                        <div className="form-floating">
                                            <input
                                                type="tel"
                                                id="monumentPhone"
                                                name="monumentPhone"
                                                className={`form-control ${errors.phoneNumber ? 'is-invalid' : ''}`}
                                                value={phoneNumber}
                                                onChange={(e) => {
                                                    setPhoneNumber(e.target.value);
                                                    if (errors.phoneNumber || errors.contact) setErrors({ ...errors, phoneNumber: "", contact: "" });
                                                }}
                                                placeholder="Ex: 9034675791"
                                                disabled={isSubmitting}
                                            />
                                            <label htmlFor="monumentPhone">
                                                <i className="bi bi-telephone text-primary me-2"></i>
                                                Phone Number
                                            </label>
                                            {errors.phoneNumber && <div className="invalid-feedback d-block">{errors.phoneNumber}</div>}
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="form-floating">
                                            <input
                                                type="email"
                                                id="monumentEmail"
                                                name="monumentEmail"
                                                className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                                                value={email}
                                                onChange={(e) => {
                                                    setEmail(e.target.value);
                                                    if (errors.email || errors.contact) setErrors({ ...errors, email: "", contact: "" });
                                                }}
                                                placeholder="name@example.com"
                                                disabled={isSubmitting}
                                            />
                                            <label htmlFor="monumentEmail">
                                                <i className="bi bi-envelope text-primary me-2"></i>
                                                Email
                                            </label>
                                            {errors.email && <div className="invalid-feedback d-block">{errors.email}</div>}
                                        </div>
                                    </div>
                                </div>
                                <div className="mb-3">
                                    {errors.contact
                                        ? <small className="text-danger">{errors.contact}</small>
                                        : <small className="text-muted">Enter a phone number or an email.</small>}
                                </div>

                                {/* Unit type and lead source — both optional. */}
                                <div className="row g-3 mb-3">
                                    <div className="col-md-6">
                                        <div className="form-floating">
                                            <select
                                                className="form-select"
                                                id="unitGroupUuid"
                                                value={unitGroupUuid}
                                                onChange={(e) => setUnitGroupUuid(e.target.value)}
                                                disabled={isSubmitting}
                                            >
                                                <option value="">Select a unit type…</option>
                                                {unitGroups.map((g) => (
                                                    <option key={g.unitGroupUuid} value={g.unitGroupUuid}>{g.label}</option>
                                                ))}
                                            </select>
                                            <label htmlFor="unitGroupUuid">
                                                <i className="bi bi-box-seam text-primary me-2"></i>
                                                Unit Type
                                            </label>
                                        </div>
                                        {!providerLoaded && (
                                            <div className="form-text text-muted">Loading unit types…</div>
                                        )}
                                        {providerLoaded && unitGroups.length === 0 && (
                                            <div className="form-text text-muted">No unit types are available for this facility — you can still create the lead. Ask an admin to check the setup.</div>
                                        )}
                                    </div>
                                    <div className="col-md-6">
                                        <div className="form-floating">
                                            <select
                                                className="form-select"
                                                id="leadSourceUuid"
                                                value={leadSourceUuid}
                                                onChange={(e) => setLeadSourceUuid(e.target.value)}
                                                disabled={isSubmitting}
                                            >
                                                <option value="">Select a lead source…</option>
                                                {leadSources.map((s) => (
                                                    <option key={s.leadSourceUuid} value={s.leadSourceUuid}>{s.description}</option>
                                                ))}
                                            </select>
                                            <label htmlFor="leadSourceUuid">
                                                <i className="bi bi-megaphone text-primary me-2"></i>
                                                Lead Source
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                )}
            </form>
        </AppModal>
    );
});

VideoCallCustomerDetails.displayName = 'VideoCallCustomerDetails';

export default VideoCallCustomerDetails;